import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { UIButton, UIEmptyState, UITable } from "../../../ui";
import UIStatusTag from "../../../ui/UIStatusTag";
import { APPLICANT_EVALUATION_STATUSES } from "./constants";
import { applicantNameSortComparator, roleSortComparator } from "./applicantEvaluationsSorting";

const statusToneMap = {
  [APPLICANT_EVALUATION_STATUSES.pending]: "warningSoft",
  [APPLICANT_EVALUATION_STATUSES.done]: "successSoft",
};

const ApplicantEvaluationsTable = ({
  rows,
  labels,
  sortModel,
  onSortModelChange,
  onViewResult,
  onEvaluate,
  readOnly,
  canSubmit,
  canSubmitAll,
  isLoading,
  isFiltered,
}) => {
  const columns = useMemo(
    () => [
      {
        field: "applicantName",
        headerName: labels.applicantName,
        minWidth: 160,
        flex: 1,
        sortable: true,
        sortingOrder: ["asc", "desc"],
        sortComparator: applicantNameSortComparator,
      },
      {
        field: "role",
        headerName: labels.role,
        minWidth: 120,
        sortable: true,
        sortingOrder: ["asc", "desc"],
        sortComparator: roleSortComparator,
      },
      {
        field: "zoneName",
        headerName: labels.zoneName,
        minWidth: 130,
        sortable: false,
      },
      {
        field: "status",
        headerName: labels.status,
        minWidth: 120,
        sortable: false,
        renderCell: ({ value }) => (
          <UIStatusTag
            status={value}
            label={labels.statuses[value] || value}
            tone={statusToneMap[value]}
            preset="table"
          />
        ),
      },
      {
        field: "actions",
        headerName: labels.actions,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => {
          const isDone = row.status === APPLICANT_EVALUATION_STATUSES.done;

          // View-only (admin) portal: always show "View result", disabled while pending.
          if (readOnly) {
            return (
              <UIButton
                variant="outline"
                size="small"
                style={{ minWidth: 140 }}
                disabled={!isDone}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewResult(row);
                }}
              >
                {labels.viewResult}
              </UIButton>
            );
          }

          if (isDone) {
            return (
              <UIButton
                variant="outline"
                size="small"
                style={{ minWidth: 140 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewResult(row);
                }}
              >
                {labels.viewResult}
              </UIButton>
            );
          }

          // Company admins may submit for any applicant; project managers
          // (canSubmit without canSubmitAll) are restricted to supervisor
          // forms only (backend enforces this too).
          if (!canSubmit) return null;
          if (!canSubmitAll && !row.isSupervisor) return null;

          return (
            <UIButton
              variant="primary"
              appearance="outline"
              size="small"
              style={{ minWidth: 140 }}
              onClick={(e) => {
                e.stopPropagation();
                onEvaluate(row);
              }}
            >
              {labels.evaluate}
            </UIButton>
          );
        },
      },
    ],
    [labels, onViewResult, onEvaluate, readOnly, canSubmit, canSubmitAll],
  );

  if (!rows.length) {
    // Suppress the empty state while the fetch is in-flight: stale Redux data
    // from a previously visited round can produce 0 filtered rows, causing a
    // UIEmptyState → UITable flash once the real response arrives.
    if (isLoading) return null;
    return (
      <UIEmptyState
        title={isFiltered ? labels.noResultsTitle : labels.emptyTitle}
        description={isFiltered ? labels.noResultsDescription : labels.emptyDescription}
      />
    );
  }

  return (
    <UITable
      rows={rows}
      columns={columns}
      contentSized
      fillAvailableWidth
      hideFooter
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};

ApplicantEvaluationsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  labels: PropTypes.shape({
    applicantName: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    zoneName: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    actions: PropTypes.string.isRequired,
    viewResult: PropTypes.string.isRequired,
    evaluate: PropTypes.string.isRequired,
    emptyTitle: PropTypes.string.isRequired,
    emptyDescription: PropTypes.string.isRequired,
    noResultsTitle: PropTypes.string.isRequired,
    noResultsDescription: PropTypes.string.isRequired,
    statuses: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  sortModel: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      sort: PropTypes.oneOf(["asc", "desc"]),
    }),
  ).isRequired,
  onSortModelChange: PropTypes.func.isRequired,
  onViewResult: PropTypes.func.isRequired,
  onEvaluate: PropTypes.func,
  readOnly: PropTypes.bool,
  canSubmit: PropTypes.bool,
  canSubmitAll: PropTypes.bool,
  isLoading: PropTypes.bool,
  isFiltered: PropTypes.bool,
};

ApplicantEvaluationsTable.defaultProps = {
  onEvaluate: undefined,
  readOnly: false,
  canSubmit: false,
  canSubmitAll: false,
  isLoading: false,
  isFiltered: false,
};

export default ApplicantEvaluationsTable;
